import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { WorkspaceController, type WorkspaceWindow } from "./workspaceController";
import type { Workspace, WorkspaceSnapshot } from "./workspaceTypes";

/** Groups side by side from this width; narrower screens show the active group alone (WA-10). */
const WIDE_SCREEN_QUERY = "(min-width: 1024px)";

function subscribeToWidth(onChange: () => void): () => void {
  const query = window.matchMedia(WIDE_SCREEN_QUERY);
  query.addEventListener("change", onChange);
  return () => { query.removeEventListener("change", onChange); };
}

const isWide = () => window.matchMedia(WIDE_SCREEN_QUERY).matches;

export interface WorkspaceOwner {
  readonly controller: WorkspaceController;
  readonly snapshot: WorkspaceSnapshot;
  /** The workspace outside the tabs (top bar, window-wide providers): the active tab's. */
  readonly chrome: Workspace;
  readonly wide: boolean;
}

/** The workspace owner for one signed-in person in this window (WA-10, F90); the layout is theirs. */
export function useWorkspaceOwner(browser: WorkspaceWindow, accountId: string): WorkspaceOwner {
  const [controller] = useState(() => new WorkspaceController(browser, `dmw.workspace.tabs.${accountId}`));
  useEffect(() => controller.attach(), [controller]);
  const wide = useSyncExternalStore(subscribeToWidth, isWide);
  useEffect(() => { controller.setNarrow(!wide); }, [controller, wide]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const chrome = useMemo(() => controller.workspaceOf(snapshot.activeTab, true), [controller, snapshot.activeTab]);
  return { controller, snapshot, chrome, wide };
}
